/**
 * High-level API for cross-chain analysis
 */

import {
  hiveClient,
  steemitClient,
  blurtClient,
} from './rpcClient';
import {
  CrossPostMatch,
  ChainActivityStatus,
  ChainAccount,
  HivePost,
} from '../types/index';
import { toDateOrNull, maxDate } from '../utils/dateHelpers';

const LOOKBACK_HOURS = 24;
const ACCOUNTS_BATCH_SIZE = 100;
const FOLLOWING_BATCH_SIZE = 100;

async function getAccountsInBatches(
  usernames: string[],
  client: typeof hiveClient
): Promise<ChainAccount[]> {
  const chunks: string[][] = [];
  for (let i = 0; i < usernames.length; i += ACCOUNTS_BATCH_SIZE) {
    chunks.push(usernames.slice(i, i + ACCOUNTS_BATCH_SIZE));
  }

  const responses = await Promise.all(
    chunks.map((chunk) => client.get_accounts(chunk).catch(() => []))
  );
  return responses.flat();
}

async function getChainActivity(
  username: string,
  client: typeof hiveClient
): Promise<ChainActivityStatus> {
  try {
    const accounts = await client.get_accounts([username]);
    
    if (!accounts || accounts.length === 0) {
      return { active: false, last: undefined, sourceField: undefined, error: false };
    }

    const account = accounts[0];
    const lastVote = toDateOrNull(account.last_vote_time);
    const lastPost = toDateOrNull(account.last_post);
    const lastRoot = toDateOrNull(account.last_root_post);
    const last = maxDate([lastVote, lastPost, lastRoot]);

    const isActive =
      last ? Date.now() - last.getTime() <= LOOKBACK_HOURS * 3600000 : false;

    let sourceField: string | null = null;
    if (last) {
      if (lastVote && +last === +lastVote) sourceField = 'last_vote_time';
      else if (lastPost && +last === +lastPost) sourceField = 'last_post';
      else if (lastRoot && +last === +lastRoot) sourceField = 'last_root_post';
    }

    return { active: isActive, last, sourceField, error: false };
  } catch (error) {
    console.error(`Error checking activity for ${username}:`, error);
    return { active: false, error: true };
  }
}

function calculateIdentityMatch(
  hivePost: HivePost,
  steemitPost?: Partial<HivePost>,
  blurtPost?: Partial<HivePost>
): number {
  let score = 0;

  // Check Steemit match
  if (steemitPost) {
    if (steemitPost.permlink === hivePost.permlink) score += 50;
    else if (steemitPost.title === hivePost.title) score += 30;
  }

  // Check Blurt match
  if (blurtPost) {
    if (blurtPost.permlink === hivePost.permlink) score += 50;
    else if (blurtPost.title === hivePost.title) score += 30;
  }

  return Math.min(score, 100);
}

export async function analyzePost(
  post: HivePost
): Promise<CrossPostMatch> {
  const [steemitStatus, blurtStatus] = await Promise.all([
    getChainActivity(post.author, steemitClient),
    getChainActivity(post.author, blurtClient),
  ]);

  // Check for identical content on other chains
  let steemitContent: Partial<HivePost> | undefined;
  let blurtContent: Partial<HivePost> | undefined;

  if (steemitStatus.active || steemitStatus.sourceField === 'last_post') {
    steemitContent = (await steemitClient.get_content(
      post.author,
      post.permlink
    )) || undefined;
  }

  if (blurtStatus.active || blurtStatus.sourceField === 'last_post') {
    blurtContent = (await blurtClient.get_content(
      post.author,
      post.permlink
    )) || undefined;
  }

  const identityMatchScore = calculateIdentityMatch(
    post,
    steemitContent,
    blurtContent
  );

  return {
    author: post.author,
    hivePost: post,
    steemitStatus,
    blurtStatus,
    steemitContent,
    blurtContent,
    identityMatchScore,
  };
}

export async function analyzePostsBatch(
  posts: HivePost[]
): Promise<CrossPostMatch[]> {
  // Get all unique usernames first
  const uniqueUsernames = [...new Set(posts.map((p) => p.author))];

  // Fetch account data for all users from all chains
  const [steemitAccounts, blurtAccounts] = await Promise.all([
    getAccountsInBatches(uniqueUsernames, steemitClient),
    getAccountsInBatches(uniqueUsernames, blurtClient),
  ]);

  const accountKey = (account: { username?: string; name?: string }) =>
    (account.username || account.name || '').toLowerCase();

  const statusFromAccount = (account?: {
    last_vote_time?: string;
    last_post?: string;
    last_root_post?: string;
  }): ChainActivityStatus => {
    if (!account) {
      // Account not found on this chain is not an RPC error.
      return { active: false, error: false };
    }

    const lastVote = toDateOrNull(account.last_vote_time);
    const lastPost = toDateOrNull(account.last_post);
    const lastRoot = toDateOrNull(account.last_root_post);
    const last = maxDate([lastVote, lastPost, lastRoot]);
    const active =
      last ? Date.now() - last.getTime() <= LOOKBACK_HOURS * 3600000 : false;

    let sourceField: string | null = null;
    if (last) {
      if (lastVote && +last === +lastVote) sourceField = 'last_vote_time';
      else if (lastPost && +last === +lastPost) sourceField = 'last_post';
      else if (lastRoot && +last === +lastRoot) sourceField = 'last_root_post';
    }

    return { active, last, sourceField, error: false };
  };

  // Create a map of username -> account data
  const steemitMap = new Map(
    (steemitAccounts || [])
      .map((a) => [accountKey(a), a] as const)
      .filter(([key]) => key.length > 0)
  );
  const blurtMap = new Map(
    (blurtAccounts || [])
      .map((a) => [accountKey(a), a] as const)
      .filter(([key]) => key.length > 0)
  );

  // Analyze each post with cached account data
  const results = await Promise.all(
    posts.map(async (post) => {
      const authorKey = post.author.toLowerCase();
      const steemitAccount = steemitMap.get(authorKey);
      const blurtAccount = blurtMap.get(authorKey);

      const steemitStatus = statusFromAccount(steemitAccount);
      const blurtStatus = statusFromAccount(blurtAccount);

      // Check for identical content
      let steemitContent: Partial<HivePost> | undefined;
      let blurtContent: Partial<HivePost> | undefined;

      if (steemitStatus.active || steemitStatus.sourceField === 'last_post') {
        steemitContent = (await steemitClient.get_content(
          post.author,
          post.permlink
        ).catch(() => null)) || undefined;
      }

      if (blurtStatus.active || blurtStatus.sourceField === 'last_post') {
        blurtContent = (await blurtClient.get_content(
          post.author,
          post.permlink
        ).catch(() => null)) || undefined;
      }

      return {
        author: post.author,
        hivePost: post,
        steemitStatus,
        blurtStatus,
        steemitContent,
        blurtContent,
        identityMatchScore: calculateIdentityMatch(
          post,
          steemitContent,
          blurtContent
        ),
      };
    })
  );

  return results;
}

export async function fetchRecentHivePosts(hours: number = 24): Promise<HivePost[]> {
  return fetchRecentHivePostsWithOptions(hours, {});
}

interface FetchPostsOptions {
  stopAtPostKey?: string;
}

export async function fetchRecentHivePostsWithOptions(
  hours: number = 24,
  options: FetchPostsOptions = {}
): Promise<HivePost[]> {
  try {
    console.log(`[HIVE] Fetching posts from last ${hours} hours...`);
    const MAX_PAGES = 100;
    // condenser_api.get_discussions_by_created supports max limit 20.
    const PAGE_SIZE = 20;
    const cutoffTime = Date.now() - hours * 3600000;
    const seen = new Set<string>();
    const recentPosts: HivePost[] = [];

    let startAuthor: string | undefined;
    let startPermlink: string | undefined;
    let reachedStopKey = false;

    for (let page = 0; page < MAX_PAGES; page++) {
      const batch = await hiveClient.get_discussions_by_created('', PAGE_SIZE, {
        startAuthor,
        startPermlink,
      });
      if (!batch || batch.length === 0) break;

      for (const post of batch) {
        const key = `${post.author}/${post.permlink}`;
        if (seen.has(key)) continue;
        seen.add(key);

        if (options.stopAtPostKey && key === options.stopAtPostKey) {
          reachedStopKey = true;
          break;
        }

        const postDate = toDateOrNull(post.created);
        if (postDate && postDate.getTime() > cutoffTime) {
          recentPosts.push(post);
        }
      }

      if (reachedStopKey) break;

      const last = batch[batch.length - 1];
      if (!last?.author || !last?.permlink) break;

      startAuthor = last.author;
      startPermlink = last.permlink;

      const lastDate = toDateOrNull(last.created);
      if (lastDate && lastDate.getTime() <= cutoffTime) break;

      // Small delay between pages to avoid rate limiting.
      await new Promise((r) => setTimeout(r, 100));
    }

    console.log(
      `[HIVE] Total unique recent posts (last ${hours}h): ${recentPosts.length}, reachedStopKey=${reachedStopKey}`
    );

    // Final guard: keep only posts from the last N hours.
    const filteredPosts = recentPosts.filter((post) => {
      const postDate = toDateOrNull(post.created);
      return !!postDate && postDate.getTime() > cutoffTime;
    });

    console.log(`[HIVE] Recent posts (last ${hours}h): ${filteredPosts.length}`);
    if (filteredPosts.length === 0) {
      console.warn(`[HIVE] No posts found in the last ${hours} hours`);
      console.log(
        '[HIVE] Sample recent posts:',
        recentPosts.slice(0, 3).map((p) => ({ author: p.author, created: p.created }))
      );
    }

    return filteredPosts;
  } catch (error) {
    console.error('[HIVE] Failed to fetch recent posts:', error);
    throw error;
  }
}

export async function fetchMutedAuthors(username: string): Promise<Set<string>> {
  const muted = new Set<string>();
  const cleanUsername = username.trim().replace(/^@/, '').toLowerCase();

  if (!cleanUsername) {
    return muted;
  }

  try {
    let startFollowing = '';

    for (let page = 0; page < 200; page++) {
      const entries = await hiveClient.get_following(
        cleanUsername,
        startFollowing,
        'ignore',
        FOLLOWING_BATCH_SIZE
      );

      if (!entries || entries.length === 0) break;

      for (const entry of entries) {
        const following = (entry.following || '').toLowerCase();
        if (!following) continue;
        if (startFollowing && following === startFollowing.toLowerCase()) continue;
        muted.add(following);
      }

      const lastFollowing = entries[entries.length - 1]?.following?.toLowerCase();
      if (!lastFollowing || lastFollowing === startFollowing.toLowerCase()) break;

      startFollowing = lastFollowing;
      if (entries.length < FOLLOWING_BATCH_SIZE) break;
    }
  } catch (error) {
    console.error(`[HIVE] Failed to fetch muted list for ${cleanUsername}:`, error);
    throw error;
  }

  return muted;
}
